import { coerceNumberProperty } from '@angular/cdk/coercion';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatChipGrid, MatChipInputEvent } from '@angular/material/chips';
import { MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { Store } from '@ngrx/store';
import { EnpsControlComponent } from '@src/suite/common/controls/enps-control/enps-control';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { optionContainsTerm } from '@src/suite/common/utils/util';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
    selector: 'st-staff-id-list',
    templateUrl: './staff-id-list.html',
    styleUrls: ['./staff-id-list.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'st-staff-id-list',
    },
})
export class StaffIDListComponent extends EnpsControlComponent<string[]> implements OnInit {
    store = inject(Store);

    i18N = this.store.selectSignal(selectI18N);

    shouldShowAddButton = input(true);
    maxItemsToShow = input(3, {
        transform: val => coerceNumberProperty(val, 3),
    });
    internalValue = signal<string[]>([]);
    isListOpen = signal(false);

    overlay = viewChild(CdkConnectedOverlay);
    chipGrid = viewChild<MatChipGrid>('chipGrid');
    selectionList = viewChild(MatSelectionList);

    staffFilter = new FormControl('');
    filteredOptions: Observable<string[]>;

    allUsers = computed(() => Object.values(this.globalTablesService.Staff));
    searchLabel = computed(() => `${`${this.i18N().PSTWebSearchLabel}...`}`);
    extraStaffCount = computed(() => this.internalValue().length - this.maxItemsToShow());
    shouldShowExtraStaff = computed(() => this.extraStaffCount() > 0);

    indexes = computed(() => {
        const length = this.shouldShowExtraStaff() ? this.maxItemsToShow() : this.internalValue().length;
        return Array.from({ length }, (_, i) => i);
    });
    isCompactView = computed(() => this.maxItemsToShow() === 0);
    hasButtonLabel = computed(() => this.internalValue()?.length === 0 && !this.isCompactView());
    addButtonLabel = computed(() => (this.hasButtonLabel() ? `${this.label()}` : ''));

    constructor() {
        super();
        effect(
            () => {
                this.internalValue.set([...(this.value() || [])]);
            },
            { allowSignalWrites: true }
        );
    }

    ngOnInit() {
        this.filteredOptions = this.staffFilter.valueChanges.pipe(
            startWith(''),
            map(value => this._filter(value || ''))
        );
    }

    public displayNameByKey(key: string): string {
        return this.globalTablesService.Staff && this.globalTablesService.Staff[key] ? `${this.globalTablesService.Staff[key].GivenName} ${this.globalTablesService.Staff[key].Surname}` : '';
    }

    valueChanged() {
        if (this.isValueChanged()) {
            this.isValueChanged.set(false);
            this.changed.emit(this.internalValue());
        }
        this.staffFilter.setValue('');
    }

    getName(userId: string) {
        const user = this.globalTablesService?.Staff[userId];
        return `${user?.GivenName} ${user?.Surname}`;
    }

    getUserInfo(userId: string) {
        const user = this.globalTablesService?.Staff[userId];
        return `${user.Email ? user.Email : ''} ${user.Title ? user.Title : ''} ${user.Phone ? user.Phone : ''}`;
    }

    tooltipUsers = computed(() => {
        const users = this.internalValue()?.slice(this.indexes().length);
        if (users.length > 0) {
            return this.allUsers()
                .filter(option => users.includes(option.UserID))
                .map(item => item);
        }
    });

    private _filter(value: string): string[] {
        return this.allUsers()
            .filter(option => optionContainsTerm(option.UserID, value) || optionContainsTerm(option.DisplayName, value))
            .sort((a, b) => a.DisplayName?.localeCompare(b?.DisplayName))
            .map(item => item.UserID);
    }

    add(event: MatChipInputEvent): void {
        const value = (event.value || '').trim().toLowerCase();
        const data = this.allUsers().find(option => {
            if (value) {
                const filteredUser = optionContainsTerm(option.UserID, value) || optionContainsTerm(option.DisplayName, value);
                return filteredUser;
            }
            return undefined;
        });
        if (data) {
            this.internalValue.set([...(this.internalValue() || []), data.UserID]);
        }

        event.chipInput!.clear();
        this.isValueChanged.set(true);

        this.staffFilter.setValue('');
    }

    remove(val: string): void {
        const index = this.internalValue().indexOf(val);

        if (index >= 0) {
            this.internalValue.update(values => {
                values?.splice(index, 1);
                return [...(values || [])];
            });
        }
        this.isValueChanged.set(true);
    }

    selected(event: MatSelectionListChange): void {
        const selectedUser = this.internalValue().find(option => option === event.options[0].value);
        if (selectedUser) return;

        this.internalValue.set([...(this.internalValue() || []), event.options[0].value]);
        this.searchBox().nativeElement.value = '';
        this.staffFilter.setValue('');
        this.isValueChanged.set(true);
    }

    showStaffTooltip() {
        this.isListOpen.set(true);
    }

    onMouseOut(event: MouseEvent) {
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-staff-id-list-tooltip-content')) {
            this.isListOpen.set(false);
        }
    }

    panelAttached() {
        const overlayElement = this.overlay()?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener('mouseleave', () => {
                this.isListOpen.set(false);
            });
        }
    }

    public getItemInitials(item: any) {
        return this.allUsers().find(option => option.UserID.toLowerCase().includes(item?.toLowerCase()))?.Initials;
    }

    onUpArrowPress() {
        if (this.chipGrid()._chips.length === 0) {
            this.selectionList().options.last.focus();
        } else {
            this.chipGrid()._chips.first.focus();
        }
    }

    onDownArrowPress() {
        this.selectionList()?.options?.first?.focus();
    }
}
